/**
 * Arrastre manual (ratón o dedo) sobre el carrusel infinito de gatos.
 *
 * La pista (.kotashi-gatos__pista) ya gira sola por CSS (@keyframes, ver
 * sections/kotashi-gatos.liquid). Este script solo toma el control mientras el usuario
 * arrastra con el puntero (Pointer Events: cubre ratón, dedo y lápiz con el mismo código),
 * y al soltar retoma la animación automática desde el mismo punto exacto donde quedó — con
 * un animation-delay negativo, no reiniciándola — para que no haya ningún salto visual.
 *
 * 17 agosto 2026: primera versión usaba animation-play-state:paused durante el arrastre.
 * Bug real: CSS da prioridad a una animación (aunque esté en pausa) sobre un transform
 * puesto a mano por JS en el mismo elemento — así que el transform de cada pointermove se
 * calculaba bien pero el navegador lo ignoraba, y por fuera se veía "congelado" hasta
 * soltar, momento en el que se leía la posición (la de la animación pausada, no la
 * arrastrada de verdad) y aparentaba un salto/teletransporte. Arreglo: apagar la animación
 * del todo (animation:none) mientras se arrastra, no solo pausarla, para que el transform
 * en línea mande de verdad fotograma a fotograma.
 *
 * Robustez:
 * - Pointer Events en vez de mouse+touch por separado: un solo listener cubre los dos casos.
 * - touch-action:pan-y en el CSS dice al navegador "el scroll vertical de la página sigue
 *   siendo suyo, el horizontal lo llevo yo": así no compiten el gesto nativo y este script.
 * - No se recorta/envuelve la posición MIENTRAS se arrastra (solo al soltar, vía
 *   animation-delay): envolver a media pulsión daría un salto brusco bajo el dedo del
 *   usuario, justo lo que este script existe para evitar.
 * - Sigue funcionando con "reducir movimiento" activado: sin animación de fondo que
 *   retomar, el contenido simplemente se queda donde se suelte.
 */
(() => {
  const leerTranslateX = (elemento) => {
    const transformacion = getComputedStyle(elemento).transform;
    if (transformacion === 'none') return 0;
    const coincidencia = transformacion.match(/matrix\(([^)]+)\)/);
    if (!coincidencia) return 0;
    const partes = coincidencia[1].split(',').map(Number);
    return partes[4] || 0;
  };

  const activar = (pista) => {
    let arrastrando = false;
    let inicioX = 0;
    let inicioTranslateX = 0;
    let anchoGrupo = 0;
    let duracionOriginal = 0;

    pista.addEventListener('pointerdown', (evento) => {
      // En ratón, solo el botón principal; con dedo/lápiz siempre entra.
      if (evento.pointerType === 'mouse' && evento.button !== 0) return;

      // Leer la posición y la duración ANTES de tocar nada: en este instante la
      // animación todavía manda, así que getComputedStyle da el valor real y visible.
      inicioTranslateX = leerTranslateX(pista);
      duracionOriginal = parseFloat(getComputedStyle(pista).animationDuration) || 0;

      arrastrando = true;
      inicioX = evento.clientX;
      // Hay 4 copias idénticas del contenido (ver el bucle "for i in (1..4)" en el
      // Liquid): un grupo es un cuarto del ancho total de la pista.
      anchoGrupo = pista.scrollWidth / 4;

      // Apagar la animación del todo (no solo pausarla, ver nota arriba) y fijar el
      // transform al mismo valor donde iba, para que no haya ni un parpadeo al tomar
      // el control.
      pista.style.animation = 'none';
      pista.style.transform = `translateX(${inicioTranslateX}px)`;
      pista.classList.add('is-arrastrando');
      pista.setPointerCapture(evento.pointerId);
    });

    pista.addEventListener('pointermove', (evento) => {
      if (!arrastrando) return;
      const delta = evento.clientX - inicioX;
      pista.style.transform = `translateX(${inicioTranslateX + delta}px)`;
    });

    const soltar = (evento) => {
      if (!arrastrando) return;
      arrastrando = false;
      pista.classList.remove('is-arrastrando');
      if (pista.hasPointerCapture(evento.pointerId)) {
        pista.releasePointerCapture(evento.pointerId);
      }

      // Con animation:none todavía puesto, esto lee el transform real que se ve en
      // pantalla (el del último pointermove), no un valor de animación pausada.
      const xFinal = leerTranslateX(pista);

      pista.style.transform = '';
      // Quita el animation:none en línea: la regla del stylesheet (con el nombre de la
      // animación) vuelve a mandar, y de paso reactiva animation-play-state:running si
      // se había tocado alguna vez (no queda ningún resto en línea de por medio).
      pista.style.animation = '';

      if (duracionOriginal > 0 && anchoGrupo > 0) {
        // Qué fracción del ciclo (0 a 1) representa la posición donde se soltó, y ese
        // mismo punto aplicado como retraso negativo: el navegador "adelanta" el reloj
        // de la animación hasta ahí sin mover nada de golpe. Funciona con cualquier
        // valor (incluso fuera de 0..1): con iteración infinita, CSS lo interpreta en
        // bucle igualmente.
        const fraccion = xFinal / -anchoGrupo;
        pista.style.animationDelay = `${-(fraccion * duracionOriginal)}s`;
      }
    };

    pista.addEventListener('pointerup', soltar);
    pista.addEventListener('pointercancel', soltar);
  };

  document.querySelectorAll('.kotashi-gatos__pista').forEach(activar);
})();
